import { ref, computed, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'

export interface NotificationPayload {
  id?: string
  title: string
  body: string
  icon?: string
  badge?: string
  url?: string
  tag?: string
  data?: any
}

const NOTIFIED_KEY = 'kochbuch_notified_invites'

const loadNotifiedIds = (): Set<string> => {
  try {
    const raw = localStorage.getItem(NOTIFIED_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        return new Set(parsed)
      }
    }
  } catch (e) {
    console.error('Failed to load notified invitation IDs', e)
  }
  return new Set()
}

const saveNotifiedIds = (ids: Set<string>) => {
  try {
    const arr = Array.from(ids).slice(-200)
    localStorage.setItem(NOTIFIED_KEY, JSON.stringify(arr))
  } catch (e) {
    console.error('Failed to save notified invitation IDs', e)
  }
}

const notifiedIds = loadNotifiedIds()

export function useNotifications() {
  // Capture router at setup time — getCurrentInstance() is null in async callbacks
  let routerInstance: any = null
  try {
    const inst = getCurrentInstance()
    if (inst?.appContext?.app?.config?.globalProperties?.$router) {
      routerInstance = inst.appContext.app.config.globalProperties.$router
    } else if (inst) {
      routerInstance = useRouter()
    }
  } catch {}

  const getRouter = () => routerInstance

  const checkSupported = (): boolean => {
    return typeof window !== 'undefined' && 'Notification' in window
  }

  const getPermission = (): NotificationPermission | 'unsupported' => {
    if (checkSupported()) {
      return window.Notification.permission
    }
    return 'unsupported'
  }

  const permissionState = ref<NotificationPermission | 'unsupported'>(getPermission())

  const syncPermission = () => {
    permissionState.value = getPermission()
  }

  const isSupported = checkSupported()
  const isGranted = computed(() => permissionState.value === 'granted')
  const isDenied = computed(() => permissionState.value === 'denied')
  const isDefault = computed(() => permissionState.value === 'default')

  const requestPermission = async (): Promise<boolean> => {
    if (!checkSupported()) return false
    try {
      const res = await window.Notification.requestPermission()
      syncPermission()
      return res === 'granted'
    } catch (e) {
      console.error('Error requesting notification permission:', e)
      return false
    }
  }

  const hasNotifiedInvite = (id: string): boolean => {
    return notifiedIds.has(id)
  }

  const markInviteNotified = (id: string) => {
    notifiedIds.add(id)
    saveNotifiedIds(notifiedIds)
  }

  const sendNotification = async (payload: NotificationPayload): Promise<boolean> => {
    if (!checkSupported() || getPermission() !== 'granted') {
      return false
    }

    const defaultIcon = '/icon-192x192.png'
    const defaultBadge = '/icon-192x192.png'
    const url = payload.url || '/'

    // 1. Try ServiceWorker showNotification (works in PWA & background)
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator && navigator.serviceWorker.ready) {
      try {
        const reg = await navigator.serviceWorker.ready
        if (reg && typeof reg.showNotification === 'function') {
          await reg.showNotification(payload.title, {
            body: payload.body,
            icon: payload.icon || defaultIcon,
            badge: payload.badge || defaultBadge,
            tag: payload.tag || payload.id || 'general-notification',
            data: { url, ...payload.data }
          })
          if (payload.id) markInviteNotified(payload.id)
          return true
        }
      } catch (e) {
        console.warn('ServiceWorker notification failed, falling back to Notification API', e)
      }
    }

    // 2. Window Notification fallback
    try {
      const notif = new window.Notification(payload.title, {
        body: payload.body,
        icon: payload.icon || defaultIcon,
        tag: payload.tag || payload.id || 'general-notification'
      })

      notif.onclick = () => {
        try {
          window.focus()
        } catch {}
        const router = getRouter()
        if (router && payload.url) {
          router.push(payload.url)
        } else if (payload.url && window.location.pathname !== payload.url) {
          window.location.href = payload.url
        }
        notif.close()
      }

      if (payload.id) markInviteNotified(payload.id)
      return true
    } catch (e) {
      console.error('Window notification failed:', e)
      return false
    }
  }

  const notifySharedRecipeBook = async (invite: {
    id: string
    bookName?: string
    invitedByName?: string
    invitedByEmail?: string
  }) => {
    if (!invite || !invite.id || hasNotifiedInvite(invite.id)) return false
    const sender = invite.invitedByName || invite.invitedByEmail || 'Jemand'
    const bookTitle = invite.bookName || 'Kochbuch'

    return await sendNotification({
      id: invite.id,
      title: 'Neues Kochbuch geteilt 📖',
      body: `${sender} hat das Kochbuch "${bookTitle}" mit dir geteilt. Tippe hier, um es anzusehen und beizutreten.`,
      url: '/recipes',
      tag: `invite-book-${invite.id}`
    })
  }

  const notifySharedShoppingList = async (invite: {
    id: string
    listName?: string
    invitedByName?: string
    invitedByEmail?: string
  }) => {
    if (!invite || !invite.id || hasNotifiedInvite(invite.id)) return false
    const sender = invite.invitedByName || invite.invitedByEmail || 'Jemand'
    const listTitle = invite.listName || 'Einkaufsliste'

    return await sendNotification({
      id: invite.id,
      title: 'Neue Einkaufsliste geteilt 🛒',
      body: `${sender} hat die Einkaufsliste "${listTitle}" mit dir geteilt. Tippe hier, um sie anzusehen und beizutreten.`,
      url: '/shopping-list',
      tag: `invite-list-${invite.id}`
    })
  }

  const sendTestNotification = async (): Promise<boolean> => {
    const granted = isGranted.value || (await requestPermission())
    if (!granted) return false

    return await sendNotification({
      title: 'Kochbuch & Einkaufsliste 🔔',
      body: 'Benachrichtigungen sind erfolgreich aktiviert! Du wirst über geteilte Listen und Kochbücher informiert.',
      url: '/recipes',
      tag: 'test-notification-' + Date.now()
    })
  }

  return {
    isSupported,
    permission: permissionState,
    isGranted,
    isDenied,
    isDefault,
    requestPermission,
    sendNotification,
    notifySharedRecipeBook,
    notifySharedShoppingList,
    sendTestNotification,
    hasNotifiedInvite,
    markInviteNotified
  }
}
